import React, {useState} from 'react';
import {useParams} from "react-router-dom";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {InputSwitch} from "primereact/inputswitch";
import NetworkWrapper from "../NetworkWrapper";
import {getFromHub, postToHub} from "../../network/actions";
import {useGet} from "../../network/useGet";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {useNotificationContext} from "../../context/NotificationContext";
import {ConfirmDialog} from "primereact/confirmdialog";
import {downloadExport} from "../../utils/exportUtils";
import {getCurrentDateTimeForFilename} from "../../utils/dateUtils";
import { FilterMatchMode } from 'primereact/api';

const UndoToast = ({onClick}) => {
    return (
        <div className="font-medium text-lg my-3 w-full">
            <p className="mb-3">Need to undo this action?</p>
            <Button
                label="Undo"
                severity="info"
                onClick={onClick}
            />
        </div>
    )
}

const UndoConfirmedToast = () => {
    return (
        <div className="font-medium text-lg my-3 w-full">
            <p>Action successfully undone</p>
        </div>
    )
}

function Mustering() {
    const { toast } = useNotificationContext();
    const [reloadCount, setReloadCount] = useState(0);
    const [includeCompleted, setIncludeCompleted] = useState(false);
    const [toEdit, setToEdit] = useState({});
    const [showDialog, setShowDialog] = useState("");

    const params = useParams();
    const isRiver = params?.type === "river";
    const dataUrl = includeCompleted ? 'muster/admin/allPassengers' : 'muster/admin/allPassengers?completed=false';

    const { isLoading, data, error } = useGet(dataUrl, reloadCount);

    const getField = (fieldName, rowData) => {
        switch (fieldName) {
            case "isVideoWatched": {
                const newValue = !rowData?.isVideoWatched;
                const updateUrl = 'muster/admin/updateVideoSeen';
                return {
                    inputSwitchValue: rowData?.isVideoWatched,
                    updateUrl,
                    updatePayload: {accountId: rowData?.accountNumber, isVideoWatched: newValue},
                    toastTemplate: {
                        success:
                            <div>
                                <div className="font-medium text-lg my-3">
                                    <p>Set safety video watched status: <b>{String(newValue)}</b> for passenger <b>{rowData.name}</b></p>
                                </div>
                            </div>,
                        undo:
                            <UndoToast onClick={() =>
                                handleConfirm({
                                    updateUrl,
                                    updatePayload: {accountId: rowData?.accountNumber, isVideoWatched: !newValue},
                                    toastTemplate: {success: <UndoConfirmedToast/>}
                                })
                            }/>
                    }
                }
            }
            case "isMusterScanPerformed": {
                const newValue = !rowData?.isMusterScanPerformed;
                const updateUrl = 'muster/admin/updateStationVisit';
                return {
                    inputSwitchValue: rowData?.isMusterScanPerformed,
                    updateUrl,
                    updatePayload: {accountId: rowData?.accountNumber, stationVisited: newValue},
                    toastTemplate: {
                        success:
                            <div>
                                <div className="font-medium text-lg my-3 text-900">Set muster station visited: <b>{String(newValue)}</b> for passenger <b>{rowData.name}</b></div>
                            </div>,
                        undo:
                            <UndoToast onClick={() =>
                                handleConfirm({
                                    updateUrl,
                                    updatePayload: {accountId: rowData?.accountNumber, stationVisited: !newValue},
                                    toastTemplate: {success: <UndoConfirmedToast/>}
                                })
                            }/>
                    }
                }
            }
            default:
                return {
                    inputSwitchValue: rowData?.isBlocked,
                    dialogText: `You are about to ${rowData?.isBlocked ? "unblock" : "block"} ${rowData?.name}`,
                    updateUrl: 'muster/admin/updateAccountAccess',
                    updatePayload: { passengers: [{accountId: rowData?.accountNumber, blockAccount: !rowData?.isBlocked}] }
                }
        }
    }

    function inputSwitchField(rowData, fieldName) {
        const field = getField(fieldName, rowData);
        return <InputSwitch
            checked={field.inputSwitchValue}
            onChange={() => {
                if (field.dialogText) {
                    showConfirmation(rowData, fieldName)
                } else {
                    //Skip dialog
                    handleConfirm(field);
                }
            }} />;
    }

    const showConfirmation = (rowData, fieldName) => {
        // store the row data and fieldName to use in all other helper methods and show modal
        setToEdit({rowData, fieldName});
        setShowDialog("single");
    }

    const renderFooter = () => {
        return (
            <div className="flex justify-between">
                <Button label="No" icon="pi pi-times" onClick={hideDialog} text />
                <Button label="Yes" icon="pi pi-check" onClick={() => handleConfirm()} autoFocus />
            </div>
        );
    }

    const hideDialog = () => {
        setShowDialog("");
    }

    const handleConfirm = async (field) => {
        const fieldForUpdate = field ? field : getField(toEdit.fieldName, toEdit.rowData);
        if (!fieldForUpdate) {
            return;
        }
        const {updateUrl, updatePayload, toastTemplate} = fieldForUpdate;
        const response = await postToHub(updateUrl, updatePayload);

        // TODO get a better condition for response successful or not
        if(response === "") {
            if (toastTemplate) {
                toast.current.show({
                    severity: 'success',
                    content: () => toastTemplate.success,
                    life: 5000
                });
                if (toastTemplate.undo) {
                    //Delay while data reloads and user reads first msg
                    setTimeout(() => {
                        toast.current.show({
                            severity: 'info',
                            content: () => toastTemplate.undo,
                            life: 3000,
                        });
                    }, 2000);
                }
            } else {
                toast.current.show({ severity: 'success', summary: 'Update successful' });
            }
            setReloadCount((prevCount) => prevCount + 1);
        }
        else {
            toast.current.show({severity: 'error', summary: 'Error', detail: 'There has been an error. Please contact support'});
        }

        hideDialog();
    }

    const handleMassBlock = async () => {
        const {passengers: notCompletedPassengers} = await getFromHub('muster/admin/allPassengers?completed=false');

        const passengers = notCompletedPassengers
            .filter(passenger => !passenger.isBlocked)
            .map(passenger => ({accountId: passenger.accountNumber, blockAccount: true}));


        const response = await postToHub('muster/admin/updateAccountAccess', {passengers});
        if(response === "") {
            toast.current.show({severity: 'success', summary: 'Blocking successful'});
            setReloadCount(reloadCount + 1);
        }
        else {
            if(response.status === "Processed Partially") {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Some accounts could not be blocked',
                    detail: `There were ${response.UnableToBlock.length} accounts that could not be blocked in resco. Most likely system accounts.`,
                    life: 6000,
                });
                setReloadCount(reloadCount + 1);
            }
            else {
                toast.current.show({severity: 'error', summary: 'Error', detail: 'There has been an error. Please contact support'});
            }
        }


        hideDialog();
    }

    const handleExport = () => {
        const exportUrl = includeCompleted ? 'muster/admin/export' : 'muster/admin/export?completed=false';
        const fileName = `vm_export_${getCurrentDateTimeForFilename()}_${includeCompleted ? "all_passengers" : "incomplete_only"}.xls`;
        downloadExport(exportUrl, fileName);
    }

    return (
        <NetworkWrapper isLoading={isLoading} error={error}>
            {data &&
            <div className="max-w-full">
                <div className="flex">
                    <Button
                        icon="pi pi-refresh"
                        label="Refresh"
                        onClick={() => setReloadCount(reloadCount + 1)}
                    />

                    <div className="ml-4">
                        <Button
                            label={includeCompleted ? "Exclude completed" : "Include completed"}
                            onClick={() => setIncludeCompleted(!includeCompleted)}
                        />
                    </div>

                    <div className="ml-4">
                        <Button
                            label="Export"
                            onClick={handleExport}
                        />
                    </div>

                    {!isRiver &&
                    <div className="ml-4">
                        <Button
                            label="Block"
                            onClick={() => setShowDialog("massblock")}
                        />
                    </div>
                    }
                </div>

                <div className="mt-4">
                    Passengers Mustered: {data.totalCompleted}/{data.totalPax}
                </div>

                <DataTable
                    className="mt-4 p-datatable-striped text-sm lg:text-base"
                    // paginator rows={50}
                    filters={{
                        "name": { value: null, matchMode: FilterMatchMode.CONTAINS },
                        "location": { value: null, matchMode: FilterMatchMode.CONTAINS },
                        "embarkationDate": { value: null, matchMode: FilterMatchMode.CONTAINS }
                    }}
                    filterDisplay="row"
                    value={data.passengers}
                    columnResizeMode="expand"
                >
                    <Column field="name"
                            header="Name"
                            sortable={true}
                            filter={true}
                    />

                    <Column field="location"
                            header="Cabin"
                            style={{width:'15%'}}
                            sortable={true}
                            filter={true}/>

                    <Column field="isVideoWatched"
                            header="Safety video"
                            style={{width:'15%'}}
                            sortable={true}
                            body={(rowData) => inputSwitchField(rowData, "isVideoWatched")}/>

                    {!isRiver &&
                    <Column field="isMusterScanPerformed"
                            header="Muster station visited"
                            style={{width:'10%'}}
                            sortable={true}
                            body={ (rowData) => data?.config.pmsHandledMusterStationVisit ? formatBoolean(rowData, "isMusterScanPerformed") : inputSwitchField(rowData, "isMusterScanPerformed")} />
                    }

                    {!isRiver &&
                    <Column field="isMusterCompleted"
                            header="Overall status"
                            style={{width: '10%'}}
                            sortable={true}
                            body={(rowData) => formatBoolean(rowData, "isMusterCompleted")}/>
                    }

                    {!isRiver &&
                    <Column field="isBlocked"
                            header="Is blocked"
                            style={{width:'15%'}}
                            sortable={true}
                            body={(rowData) => inputSwitchField(rowData, "isBlocked")}/>
                    }
                    {!isRiver &&
                    <Column field="embarkationDate"
                            header="Embarkation date"
                            style={{width:'15%'}}
                            sortable={true}
                            filter={true}
                    />
                    }
                </DataTable>

                <Dialog
                    visible={showDialog === "single"}
                    header="Confirmation"
                    style={{ width: '60vw' }}
                    footer={renderFooter()}
                    onHide={hideDialog}
                    baseZIndex={1000}
                >
                    <div className="text-lg">
                        { getField(toEdit.fieldName, toEdit.rowData).dialogText }
                    </div>
                </Dialog>

                <ConfirmDialog
                    visible={showDialog === "massblock"}
                    header="Confirmation"
                    message="You are about to block EVERYONE who did not complete the virtual mustering. Are you sure?"
                    accept={handleMassBlock}
                    reject={hideDialog}
               />
            </div>
            }
        </NetworkWrapper>
    );
}


function formatBoolean(rowData, field) {
    return <span className={`inline-block w-6 h-6 rounded-full align-middle ${rowData[field] ? "bg-green-400" : "bg-red-400"}`}></span>;
}

export default Mustering;